import React from 'react';
import { useParams, Link } from 'react-router-dom';

const CollectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // Collection data - in a real app, this would come from an API
  const collections: Record<string, {
    id: string;
    name: string;
    category: string;
    description: string;
    longDescription: string;
    image: string;
    gradient: string;
    images: string[];
    specifications: Record<string, string>;
    features: string[];
    applications: string[];
  }> = {
    'suits': {
      id: 'suits',
      name: 'Tailored Suits',
      category: 'suits',
      description: 'Premium suits for the boardroom, weddings, and formal occasions',
      longDescription: 'Our tailored suits are cut for the modern man who demands precision and style. From classic navy and charcoal to contemporary fits, each piece is crafted with premium fabrics and construction. Available for order and fitting at our Manhattan showroom.',
      image: '/images/collection/suits.jpg',
      gradient: 'from-royal-purple to-amethyst',
      images: ['/images/collection/suits.jpg'],
      specifications: {
        'Fabric': 'Super 100s–120s Wool & Blends',
        'Fit': 'Slim, Regular, Classic',
        'Lining': 'Full or half lining',
        'Care': 'Dry clean recommended',
        'Origin': 'Tailored in Manhattan, NY'
      },
      features: [
        'Premium wool and wool-blend fabrics',
        'Multiple fit options',
        'Hand-finished details',
        'Customization available',
        'Expert fitting in-store',
        'Timeless construction'
      ],
      applications: ['Boardroom', 'Weddings', 'Interviews', 'Evening Wear', 'Business Travel']
    },
    'jerseys': {
      id: 'jerseys',
      name: 'Football Jerseys',
      category: 'jerseys',
      description: 'Match-day jerseys and club wear for players and fans',
      longDescription: 'Football jerseys built for performance and pride. Breathable, durable, and designed for the pitch or the stands. Custom club colors and personalization available. From replica to player-spec, we outfit teams and fans in Manhattan and beyond.',
      image: '/images/collection/jersey.webp',
      gradient: 'from-warm-gold to-sunset-orange',
      images: ['/images/collection/jersey.webp', '/images/collection/soccer.webp'],
      specifications: {
        'Material': 'Polyester mesh / Dri-fit style',
        'Fit': 'Standard & athletic',
        'Print': 'Sublimation & heat-applied',
        'Care': 'Machine wash cold',
        'Sizes': 'XS–XXL, custom team orders'
      },
      features: [
        'Moisture-wicking fabric',
        'Vibrant, long-lasting print',
        'Reinforced stitching',
        'Custom numbers and names',
        'Bulk team orders welcome',
        'Fan and player versions'
      ],
      applications: ['Match Day', 'Training', 'Fan Wear', 'Five-a-Side', 'Club & League']
    },
    'kits': {
      id: 'kits',
      name: 'Training Kits',
      category: 'kits',
      description: 'Performance training wear and sportswear',
      longDescription: 'Training kits engineered for movement and comfort. Lightweight, breathable, and built to perform in the gym, on the track, or on the pitch. Full kit options including tops, shorts, and base layers. Available at Veltrix Apparel, Manhattan.',
      image: '/images/collection/kits.jpeg',
      gradient: 'from-ocean-blue to-teal-wave',
      images: ['/images/collection/kits.jpeg', '/images/collection/gear.jpg'],
      specifications: {
        'Material': 'Polyester / elastane blend',
        'Fit': 'Athletic, compression options',
        'Features': 'Quick-dry, stretch',
        'Care': 'Machine wash cold',
        'Range': 'Tops, shorts, pants, base layers'
      },
      features: [
        'Quick-dry technology',
        'Four-way stretch',
        'Flatlock seams',
        'Lightweight construction',
        'Team customization',
        'Matching shorts and tops'
      ],
      applications: ['Gym', 'Running', 'Training', 'Recovery', 'Casual Sport']
    },
    'formal-wear': {
      id: 'formal-wear',
      name: 'Formal Wear',
      category: 'formal',
      description: 'Blazers, dress shirts, and evening wear',
      longDescription: 'Blazers, dress shirts, and refined evening wear for occasions that demand a sharp look. Curated in Manhattan for the modern professional and the discerning guest.',
      image: '/images/collection/formal.webp',
      gradient: 'from-coral to-rose-500',
      images: ['/images/collection/formal.webp', '/images/collection/suits.jpg'],
      specifications: {
        'Fabric': 'Wool, cotton, blends',
        'Fit': 'Slim, regular',
        'Care': 'Dry clean or machine wash',
        'Options': 'Blazers, dress shirts, trousers'
      },
      features: [
        'Premium materials',
        'Classic and modern fits',
        'Versatile styling',
        'In-store fitting',
        'Alterations available'
      ],
      applications: ['Office', 'Events', 'Dinner', 'Travel', 'Smart Casual']
    },
    'match-jerseys': {
      id: 'match-jerseys',
      name: 'Match Jerseys',
      category: 'jerseys',
      description: 'Official-style jerseys for match day and fan wear',
      longDescription: 'Match jerseys that look and perform like the real thing. Bold colors, club-ready designs, and durable construction. Order for your team or as a fan — we ship and welcome pickups in Manhattan.',
      image: '/images/collection/soccer.webp',
      gradient: 'from-deep-purple to-royal-purple',
      images: ['/images/collection/soccer.webp', '/images/collection/jersey.webp'],
      specifications: {
        'Material': 'Performance polyester',
        'Fit': 'Authentic, replica',
        'Print': 'Sublimation, names/numbers',
        'Care': 'Machine wash cold'
      },
      features: [
        'Stadium-ready design',
        'Breathable build',
        'Custom printing',
        'Bulk discounts',
        'Fast turnaround'
      ],
      applications: ['Match Day', 'Fan Wear', 'Team Kit', 'Tournaments', 'Gifts']
    },
    'training-gear': {
      id: 'training-gear',
      name: 'Training Gear',
      category: 'sport',
      description: 'Gym wear, running kits, and performance apparel',
      longDescription: 'Training gear that keeps up with you. From gym sessions to long runs, our performance apparel is built for comfort, durability, and style. Tops, shorts, and layers designed for the active man.',
      image: '/images/collection/gear.jpg',
      gradient: 'from-sky-blue to-ocean-blue',
      images: ['/images/collection/gear.jpg', '/images/collection/kits.jpeg'],
      specifications: {
        'Material': 'Polyester, nylon, elastane',
        'Fit': 'Athletic, relaxed',
        'Features': 'Quick-dry, stretch, UV',
        'Care': 'Machine wash cold'
      },
      features: [
        'Moisture management',
        'Freedom of movement',
        'Durable construction',
        'Modern cuts',
        'Range of colors'
      ],
      applications: ['Gym', 'Running', 'Cycling', 'Outdoor', 'Recovery']
    }
  };

  const collection = collections[id as keyof typeof collections];

  if (!collection) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center bg-[#fafafa]">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="font-display text-2xl font-semibold text-slate-900 mb-3">Collection not found</h1>
          <p className="text-slate-600 mb-6">The collection you're looking for doesn't exist.</p>
          <Link to="/collections" className="inline-flex px-6 py-3 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Back to collections</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Hero */}
      <section className="pt-28 pb-14 lg:pt-36 lg:pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-500 mb-10">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <span>/</span>
            <Link to="/collections" className="hover:text-slate-900">Collections</Link>
            <span>/</span>
            <span className="text-slate-900">{collection.name}</span>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div className="rounded-2xl overflow-hidden bg-slate-100 aspect-square max-h-[500px]">
              <img src={collection.image} alt={collection.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{collection.category}</span>
              <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">
                {collection.name}
              </h1>
              <p className="mt-4 text-lg text-slate-600">{collection.description}</p>
              <p className="mt-6 text-slate-600 leading-relaxed">{collection.longDescription}</p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Request sample</Link>
                <button type="button" className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">Download specs</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-semibold text-slate-900 mb-6">Gallery</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {collection.images.map((image, index) => (
              <div key={index} className="aspect-square rounded-xl overflow-hidden bg-slate-100">
                <img src={image} alt={`${collection.name} ${index + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specs & Features */}
      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <h2 className="font-display text-xl font-semibold text-slate-900 mb-6">Specifications</h2>
              <dl className="space-y-4">
                {Object.entries(collection.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-3 border-b border-slate-200">
                    <dt className="font-medium text-slate-900">{key}</dt>
                    <dd className="text-slate-600 text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold text-slate-900 mb-6">Key features</h2>
              <ul className="space-y-3">
                {collection.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </span>
                    <span className="text-slate-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-6">Perfect for</h2>
          <div className="flex flex-wrap gap-3">
            {collection.applications.map((app, index) => (
              <span key={index} className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                {app}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-semibold text-slate-900">Ready for {collection.name}?</h2>
          <p className="mt-3 text-slate-600">Visit our Manhattan showroom or get in touch for fittings and custom orders.</p>
          <Link to="/contact" className="mt-8 inline-flex items-center justify-center px-6 py-3 rounded-lg bg-slate-900 text-white font-medium text-sm hover:bg-slate-800">
            Get in touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CollectionDetailPage;
